import { get } from "./apiService";
import type {
  V2Asset,
  V2Blockchain,
  LegacyAsset,
  LegacyChain,
  LegacyPair,
  V2TradingPair,
} from "../types/api";
import { ZAR8_LOCAL_ICON_PATH } from "../utils/businessWalletAssetPresentation";
import { resolveAssetCapabilities } from "../utils/assetCapabilities";

// ─── Cache ──────────────────────────────────────────────────────────────────

interface Cache {
  allAssets: LegacyAsset[] | null;
  allBlockchains: V2Blockchain[] | null;
  assetPairs: Map<string, LegacyPair[]>;
  assetChains: Map<string, LegacyChain[]>;
  tradingPairs: V2TradingPair[] | null;
  timestamps: Map<string, number>;
}

interface PaginatedAssetResponse {
  assets?: V2Asset[];
}

const cache: Cache = {
  allAssets: null,
  allBlockchains: null,
  assetPairs: new Map(),
  assetChains: new Map(),
  tradingPairs: null,
  timestamps: new Map(),
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const pendingRequests = new Map<string, Promise<unknown>>();

const normalizeAssetList = (
  payload: V2Asset[] | PaginatedAssetResponse | null | undefined
): V2Asset[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload?.assets && Array.isArray(payload.assets)) {
    return payload.assets;
  }

  return [];
};

const isCacheValid = (key: string): boolean => {
  const ts = cache.timestamps.get(key);
  if (!ts) return false;
  return Date.now() - ts < CACHE_DURATION;
};

const setTimestamp = (key: string): void => {
  cache.timestamps.set(key, Date.now());
};

// ─── Deduplication helper ───────────────────────────────────────────────────

async function dedup<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = pendingRequests.get(key);
  if (existing) return existing as Promise<T>;

  const promise = fn().finally(() => {
    if (pendingRequests.get(key) === promise) {
      pendingRequests.delete(key);
    }
  });

  pendingRequests.set(key, promise);
  return promise;
}

// ─── V2 → Legacy Adapters ───────────────────────────────────────────────────

/**
 * Maps a v2 asset to the legacy shape that existing UI components expect.
 *
 * v2 field → legacy field:
 *   symbol → asset
 *   name → assetName
 *   image / cmc.logo → assetIcon
 *   type "crypto"/"fiat" → "Crypto"/"Fiat"
 *   status "enabled"/"disabled" → "Enabled"/"Disabled"
 */
const toLegacyAsset = (v2: V2Asset): LegacyAsset => {
  const capitalizeType = (t: string): "Crypto" | "Fiat" =>
    (t.charAt(0).toUpperCase() + t.slice(1)) as "Crypto" | "Fiat";

  const capitalizeStatus = (s: string): "Enabled" | "Disabled" =>
    (s.charAt(0).toUpperCase() + s.slice(1)) as "Enabled" | "Disabled";

  const assetIcon =
    v2.symbol.toUpperCase() === "ZAR8"
      ? ZAR8_LOCAL_ICON_PATH
      : v2.cmc?.logo || v2.image || null;
  const capabilities = resolveAssetCapabilities({
    symbol: v2.symbol,
    capabilities: v2.capabilities,
  });

  return {
    _id: v2._id,
    assetId: v2._id,
    asset: v2.symbol,
    assetName: v2.name,
    assetIcon,
    image: v2.image,
    type: capitalizeType(v2.type),
    status: capitalizeStatus(v2.status),
    canStack: v2.staking?.isStakeable ?? false,
    minStackAmount: v2.staking?.minStakeAmount ?? 0,
    maxStackAmount: v2.staking?.maxStakeAmount,
    minAmount: v2.trading?.minAmount ?? undefined,
    fees: v2.fees?.serviceFee?.feeAmount ?? undefined,
    instantPaymentFee: v2.fees?.instantPaymentFee ?? undefined,
    withdrawalFees: v2.fees?.withdrawalFees ?? undefined,
    serviceFee: v2.fees?.serviceFee
      ? {
          feeType: v2.fees.serviceFee.feeType,
          feeAmount: v2.fees.serviceFee.feeAmount,
        }
      : undefined,
    chainConfig: v2.chainConfig,
    cmc: v2.cmc,
    trading: v2.trading,
    staking: v2.staking,
    isStablecoin: v2.isStablecoin,
    notes: v2.notes,
    withdrawalNotes: v2.withdrawalNotes,
    payAddress: v2.payAddress,
    metadata: v2.metadata,
    capabilities,
    canBuy: capabilities.buy,
    canSell: capabilities.sell,
    _v2: v2,
  };
};

function getBlockchainReference(
  blockchain:
    | string
    | {
        _id?: string;
        symbol?: string;
        name?: string;
      }
): { blockchainId: string; name: string; symbol: string } {
  if (typeof blockchain === "string") {
    return {
      blockchainId: blockchain,
      name: "",
      symbol: "",
    };
  }

  return {
    blockchainId: blockchain._id ?? "",
    name: blockchain.name ?? "",
    symbol: blockchain.symbol ?? "",
  };
}

/**
 * Builds legacy chain entries from a v2 asset's chainConfig, resolved against
 * the full blockchain list.
 */
const buildLegacyChains = (
  asset: V2Asset,
  blockchains: V2Blockchain[]
): LegacyChain[] => {
  const bcMap = new Map(blockchains.map((b) => [b._id, b]));

  return (asset.chainConfig || [])
    .map((cfg): LegacyChain | null => {
      const reference = getBlockchainReference(cfg.blockchain);
      const bc = bcMap.get(reference.blockchainId);
      const name = bc?.name ?? reference.name ?? "";
      const symbol = bc?.symbol ?? reference.symbol ?? "";
      const blockchainId = reference.blockchainId;

      if (!blockchainId) {
        return null;
      }

      return {
        _id: blockchainId,
        blockchainId,
        assetId: asset._id,
        name,
        symbol,
        chain: name || symbol || blockchainId,
        networkType: bc?.networkType ?? "Mainnet",
        explorerURL: bc?.explorerURL,
        iconUrl: bc?.iconUrl,
        minimumDeposit: cfg.minimumDeposit,
        depositConfirmation: cfg.depositConfirmation,
        minimumWithdrawal: cfg.minimumWithdrawal,
        withdrawalFee: cfg.withdrawalFee,
        depositWarning: cfg.depositWarning,
        withdrawalWarning: cfg.withdrawalWarning,
        addressValidationRegex: cfg.addressValidationRegex,
        explorerUrl: cfg.explorerUrl,
        explorerTxPath: cfg.explorerTxPath,
      };
    })
    .filter((chain): chain is LegacyChain => chain !== null);
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Fetch all assets from the v2 API. Returns legacy-shaped assets for
 * backwards compatibility with existing UI components.
 *
 * Optionally filter by type ("Crypto" or "Fiat" -- legacy casing accepted).
 */
export const getAllAssets = async (
  type: string | null = null
): Promise<{ status: boolean; data: LegacyAsset[] }> => {
  const cacheKey = "allAssets";

  if (Array.isArray(cache.allAssets) && isCacheValid(cacheKey)) {
    let assets = cache.allAssets;
    if (type) {
      const normalised =
        type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      assets = assets.filter((a) => a.type === normalised);
    }
    return { status: true, data: assets };
  }

  if (cache.allAssets && !Array.isArray(cache.allAssets)) {
    cache.allAssets = null;
  }

  return dedup(cacheKey, async () => {
    // V2 ENDPOINT: GET /api/v2/assets
    const payload = await get<V2Asset[] | PaginatedAssetResponse>(
      "/api/v2/assets"
    );
    const assets = normalizeAssetList(payload);

    if (assets.length === 0) {
      throw new Error("Could not get assets");
    }

    const legacyAssets = assets.map(toLegacyAsset);
    cache.allAssets = legacyAssets;
    setTimestamp(cacheKey);

    // Also pre-build the trading pairs from the /trading/pairs endpoint
    await prefetchTradingPairs();

    let filtered = legacyAssets;
    if (type) {
      const normalised =
        type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      filtered = legacyAssets.filter((a) => a.type === normalised);
    }

    return { status: true, data: filtered };
  });
};

/**
 * Fetch a single asset by symbol.
 */
export const getAssetBySymbol = async (
  symbol: string
): Promise<LegacyAsset | null> => {
  // Try cache first
  if (cache.allAssets && isCacheValid("allAssets")) {
    const found = cache.allAssets.find(
      (a) => a.asset.toUpperCase() === symbol.toUpperCase()
    );
    if (found) return found;
  }

  // V2 ENDPOINT: GET /api/v2/assets/:symbol
  const asset = await get<V2Asset | null>(
    `/api/v2/assets/${encodeURIComponent(symbol)}`
  );

  if (!asset) return null;
  return toLegacyAsset(asset);
};

/**
 * Fetch all blockchains.
 */
export const getAllBlockchains = async (): Promise<V2Blockchain[]> => {
  const cacheKey = "allBlockchains";

  if (cache.allBlockchains && isCacheValid(cacheKey)) {
    return cache.allBlockchains;
  }

  return dedup(cacheKey, async () => {
    // V2 ENDPOINT: GET /api/v2/blockchains
    const blockchains = await get<V2Blockchain[]>("/api/v2/blockchains");
    cache.allBlockchains = blockchains;
    setTimestamp(cacheKey);
    return blockchains;
  });
};

/**
 * Get chains (networks) available for a specific asset.
 * Returns legacy chain shape for backwards compatibility.
 */
export const getAssetChains = async (
  assetId: string
): Promise<LegacyChain[]> => {
  if (!assetId) return [];

  const cacheKey = `chains_${assetId}`;

  if (cache.assetChains.has(assetId) && isCacheValid(cacheKey)) {
    return cache.assetChains.get(assetId)!;
  }

  // We need both the asset data and blockchains to build legacy chains
  const [assetsResult, blockchains] = await Promise.all([
    getAllAssets(),
    getAllBlockchains(),
  ]);

  const asset = assetsResult.data.find((a) => a._id === assetId);
  if (!asset) return [];

  const chains = buildLegacyChains(asset._v2, blockchains);
  cache.assetChains.set(assetId, chains);
  setTimestamp(cacheKey);
  return chains;
};

/**
 * Get a single blockchain by ID.
 */
export const getBlockchainById = async (
  blockchainId: string
): Promise<V2Blockchain | null> => {
  // Check cache first
  if (cache.allBlockchains && isCacheValid("allBlockchains")) {
    const found = cache.allBlockchains.find((b) => b._id === blockchainId);
    if (found) return found;
  }

  // V2 ENDPOINT: GET /api/v2/blockchains/:id
  return get<V2Blockchain | null>(`/api/v2/blockchains/${blockchainId}`);
};

// ─── Trading Pairs ──────────────────────────────────────────────────────────

/**
 * Prefetch trading pairs from the dedicated pairs endpoint.
 */
const prefetchTradingPairs = async (): Promise<void> => {
  if (cache.tradingPairs && isCacheValid("tradingPairs")) return;

  await dedup("tradingPairs", async () => {
    // V2 ENDPOINT: GET /api/v2/trading/pairs
    const pairs = await get<V2TradingPair[]>("/api/v2/trading/pairs");
    cache.tradingPairs = pairs;
    setTimestamp("tradingPairs");

    // Index pairs by assetId for quick lookups
    buildPairsCacheFromTradingPairs(pairs);
  });
};

/**
 * Derive legacy buy/sell flags from v2 pair capabilities.
 *
 * The API often sets `capabilities.buy` / `capabilities.sell` to false while
 * `capabilities.swap` is true — retail buy/sell still routes through that swap
 * rail. Without treating `swap` as tradable both ways, UI filters strip every pair.
 *
 * Disabled pairs expose no sides regardless of liquidity flags.
 */
const resolvePairTradableSides = (
  pair: V2TradingPair
): { canBuy: boolean; canSell: boolean } => {
  if (pair.enabled === false) {
    return { canBuy: false, canSell: false };
  }

  const cap = pair.capabilities;
  if (cap?.swap === true) {
    return { canBuy: true, canSell: true };
  }

  return {
    canBuy: cap?.buy ?? pair.liquidityBuyStatus !== false,
    canSell: cap?.sell ?? pair.liquiditySellStatus !== false,
  };
};

/**
 * Build per-asset pair caches from the trading pairs list.
 */
const buildPairsCacheFromTradingPairs = (pairs: V2TradingPair[]): void => {
  if (!cache.allAssets) return;

  const assetsMap = new Map<string, LegacyAsset>();
  cache.allAssets.forEach((a) => {
    assetsMap.set(a._id, a);
  });

  const grouped = new Map<string, LegacyPair[]>();

  for (const pair of pairs) {
    const assetId =
      typeof pair.assetId === "string"
        ? pair.assetId
        : (pair.assetId?._id ?? String(pair.assetId));
    if (!grouped.has(assetId)) {
      grouped.set(assetId, []);
    }

    const baseCurrencyId =
      typeof pair.baseCurrency === "string"
        ? pair.baseCurrency
        : (pair.baseCurrency?._id ?? assetId);

    const quoteCurrencyId =
      typeof pair.quoteCurrency === "string"
        ? pair.quoteCurrency
        : (pair.quoteCurrency?._id ?? "");

    const quoteAsset = assetsMap.get(quoteCurrencyId);
    const quoteCurrencyDetails =
      typeof pair.quoteCurrency === "string" ? null : pair.quoteCurrency;
    const fallbackQuoteSymbol = quoteCurrencyDetails?.symbol ?? null;
    const fallbackQuoteName = quoteCurrencyDetails?.name ?? null;
    const quoteAssetSymbol = quoteAsset?.asset ?? fallbackQuoteSymbol ?? null;
    const quoteAssetName =
      quoteAsset?.assetName ?? fallbackQuoteName ?? fallbackQuoteSymbol ?? null;

    const { canBuy, canSell } = resolvePairTradableSides(pair);

    const legacyPair: LegacyPair = {
      id: pair._id,
      _id: pair._id,
      pair: pair.pair,
      baseCurrency: baseCurrencyId,
      quoteCurrency: quoteCurrencyId,
      enabled: pair.enabled !== false,
      assetId: assetId,
      assetIcon: quoteAsset?.assetIcon ?? null,
      assetName: quoteAssetName,
      asset: quoteAssetSymbol,
      type: quoteAsset?.type ?? "Fiat",
      minBuyAmount: pair.minBuyAmount,
      minSellAmount: pair.minSellAmount,
      minBuyAmountTail: pair.minBuyAmountTail,
      minSellAmountTail: pair.minSellAmountTail,
      postTradeEnabled: pair.postTradeEnabled,
      canBuy,
      canSell,
    };

    grouped.get(assetId)!.push(legacyPair);
  }

  for (const [assetId, pairsList] of Array.from(grouped)) {
    cache.assetPairs.set(assetId, pairsList);
    setTimestamp(`pairs_${assetId}`);
  }
};

/**
 * Get trading pairs for a specific asset (by asset _id).
 * Returns legacy pair shapes.
 */
export const getAssetPairs = async (assetId: string): Promise<LegacyPair[]> => {
  if (!assetId) return [];

  const cacheKey = `pairs_${assetId}`;

  if (cache.assetPairs.has(assetId) && isCacheValid(cacheKey)) {
    return cache.assetPairs.get(assetId)!;
  }

  // Make sure assets and pairs are loaded
  await getAllAssets();

  if (cache.assetPairs.has(assetId)) {
    return cache.assetPairs.get(assetId)!;
  }

  return [];
};

/**
 * Batch get pairs for multiple assets.
 */
export const batchGetAssetPairs = async (
  assetIds: string[]
): Promise<Map<string, LegacyPair[]>> => {
  if (!assetIds || assetIds.length === 0) return new Map();

  await getAllAssets();

  const result = new Map<string, LegacyPair[]>();
  for (const id of assetIds) {
    result.set(id, cache.assetPairs.get(id) ?? []);
  }
  return result;
};

/**
 * Get all tradable pairs grouped by asset ID (crypto only).
 */
export const getAllTradablePairsOptimized = async (): Promise<
  Record<string, LegacyPair[]>
> => {
  try {
    const { data: allAssets } = await getAllAssets("Crypto");

    const assetIds = allAssets.map((a) => a._id).filter(Boolean);
    const pairsMap = await batchGetAssetPairs(assetIds);

    const result: Record<string, LegacyPair[]> = {};
    assetIds.forEach((id) => {
      result[String(id)] = pairsMap.get(id) ?? [];
    });

    return result;
  } catch (err) {
    console.error("Failed to get all tradable pairs:", err);
    return {};
  }
};

/**
 * Get the raw v2 trading pairs list.
 */
export const getTradingPairs = async (): Promise<V2TradingPair[]> => {
  if (cache.tradingPairs && isCacheValid("tradingPairs")) {
    return cache.tradingPairs;
  }

  // V2 ENDPOINT: GET /api/v2/trading/pairs
  const pairs = await get<V2TradingPair[]>("/api/v2/trading/pairs");
  cache.tradingPairs = pairs;
  setTimestamp("tradingPairs");
  return pairs;
};

// ─── Convenience Wrappers (matching old service signatures) ─────────────────

/**
 * Replaces old getTradingAssets(type) from tradingAssetsService.
 */
export const getTradingAssets = async (
  type?: string | null
): Promise<{ status: boolean; data: LegacyAsset[] }> => {
  return getAllAssets(type ?? null);
};

/**
 * Replaces old getStackableAssets().
 */
export const getStackableAssets = async (): Promise<{
  success: boolean;
  data: LegacyAsset[];
}> => {
  try {
    const result = await getAllAssets("Crypto");
    if (result?.status && result?.data) {
      const stackable = result.data.filter(
        (asset) => asset.canStack === true && asset.status === "Enabled"
      );
      return { success: true, data: stackable };
    }
    return { success: false, data: [] };
  } catch {
    return { success: false, data: [] };
  }
};

/**
 * Get the trading-asset icon URL (legacy helper).
 */
export const getTradingAssetIconURL = (image: string): string => {
  return window.location.origin + "/images/coins/" + image;
};

// ─── Cache Management ───────────────────────────────────────────────────────

export const clearCache = (key: string | null = null): void => {
  if (key) {
    if (key.startsWith("pairs_")) {
      const assetId = key.replace("pairs_", "");
      cache.assetPairs.delete(assetId);
    } else if (key.startsWith("chains_")) {
      const assetId = key.replace("chains_", "");
      cache.assetChains.delete(assetId);
    } else if (key === "allAssets") {
      cache.allAssets = null;
    } else if (key === "allBlockchains") {
      cache.allBlockchains = null;
    } else if (key === "tradingPairs") {
      cache.tradingPairs = null;
    }
    cache.timestamps.delete(key);
  } else {
    cache.allAssets = null;
    cache.allBlockchains = null;
    cache.assetPairs.clear();
    cache.assetChains.clear();
    cache.tradingPairs = null;
    cache.timestamps.clear();
  }
};

/**
 * Prefetch common data (called on app init).
 */
export const prefetchCommonAssets = async (): Promise<void> => {
  try {
    await getAllAssets("Crypto");
  } catch (err) {
    console.error("Prefetch failed:", err);
  }
};

// ─── Legacy Aliases (from tradingDataService) ────────────────────────────────

/**
 * @deprecated Combined trading data fetch (assets + wallet balances).
 * Replaces tradingDataService.getAllTradingData.
 */
export const getAllTradingData = async (type: string | null = null) => {
  const { getBalances } = await import("./walletService");
  const { getAccountId } = await import("../context/AuthActions");
  try {
    const accountId = getAccountId();
    const [tradingAssetsResponse, walletAssetsResponse] = await Promise.all([
      getAllAssets(type),
      getBalances({ accountId: accountId || undefined }),
    ]);

    return {
      tradingAssets: tradingAssetsResponse,
      walletAssets: walletAssetsResponse?.data ?? walletAssetsResponse,
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Could not get trading data: ${message}`);
  }
};
